import { useCallback, useEffect, useRef, useState } from "react";
import type { ProductionReferenceData, ProductionRun } from "../models";
import { productionGateway } from "../production-gateway";
import { operatorQuantityRecords } from "../operator-records";
import { today } from "../production-time";
import type { ProductionWorkspaceProps } from "../production-workspace-contract";

type HistoricalCorrectionTarget =
  | { kind: "quantity"; run: ProductionRun; entry: ProductionRun["entries"][number] }
  | { kind: "downtime"; run: ProductionRun; downtime: ProductionRun["downtimes"][number] };

export type HistoricalCorrectionOptions = Pick<ProductionWorkspaceProps, "user" | "notify"> & {
  close: () => void;
  onSaved: () => Promise<void>;
};

export function useHistoricalCorrectionWorkspace({ user, notify, close, onSaved }: HistoricalCorrectionOptions) {
  const [date, setDate] = useState(today());
  const [runs, setRuns] = useState<ProductionRun[]>([]);
  const [reasons, setReasons] = useState<ProductionReferenceData["reasons"]>([]);
  const [target, setTarget] = useState<HistoricalCorrectionTarget | null>(null);
  const [reason, setReason] = useState(""), [reasonCodeId, setReasonCodeId] = useState("");
  const [supervisorPin, setSupervisorPin] = useState("");
  const [loading, setLoading] = useState(true), [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const lifecycle = useRef({ live: true, read: 0, write: 0 });
  const request = useRef<AbortController | null>(null);
  useEffect(() => {
    lifecycle.current.live = true; return () => {
      lifecycle.current.live = false; lifecycle.current.read++; lifecycle.current.write++; request.current?.abort();
    };
  }, []);
  const cancelWrite = () => {
    lifecycle.current.write++; request.current?.abort(); request.current = null;
    setSupervisorPin(""); setSaving(false);
  };
  const dismiss = () => { cancelWrite(); close(); };
  const load = useCallback(async () => {
    const read = ++lifecycle.current.read;
    setLoading(true);
    try {
      const [nextRuns, reference] = await Promise.all([productionGateway.runs(date), productionGateway.referenceData(date)]);
      if (!lifecycle.current.live || lifecycle.current.read !== read) return;
      setRuns(nextRuns); setReasons(reference.reasons.filter((item) => item.category === "DOWNTIME"));
    } catch (error) {
      if (lifecycle.current.live && lifecycle.current.read === read) setFormError(error instanceof Error ? error.message : "Geçmiş kayıtlar yüklenemedi.");
    } finally { if (lifecycle.current.live && lifecycle.current.read === read) setLoading(false); }
  }, [date]);
  useEffect(() => { void load(); }, [load]);
  const choose = (next: HistoricalCorrectionTarget) => {
    cancelWrite(); setTarget(next); setFormError(null);
    setReason(next.kind === "downtime" ? next.downtime.note ?? "" : "");
    setReasonCodeId(next.kind === "downtime" ? next.downtime.reasonCodeId ?? "" : "");
  };
  const submit = async () => {
    if (!target || request.current || supervisorPin.length !== 4 || !reason.trim() || (target.kind === "downtime" && !reasonCodeId)) return;
    const controller = new AbortController(); request.current = controller;
    const write = ++lifecycle.current.write, selected = target, note = reason.trim(), code = reasonCodeId, pin = supervisorPin;
    const current = () => lifecycle.current.live && lifecycle.current.write === write && !controller.signal.aborted;
    const timeout = window.setTimeout(() => controller.abort(), 20_000);
    let mutationStarted = false;
    setSupervisorPin(""); setSaving(true); setFormError(null);
    try {
      const targetRef = selected.kind === "quantity" ? `${selected.run.id}|${selected.entry.id}` : `${selected.run.id}|${selected.downtime.id}`;
      const approval = await productionGateway.approveCriticalAction({
        supervisorId: user.id, pin,
        actionScope: selected.kind === "quantity" ? "BACKDATED_QUANTITY_CORRECTION" : "BACKDATED_DOWNTIME_CORRECTION",
        targetRef, ...(selected.kind === "downtime" ? { reasonCodeId: code, note } : { reason: note }),
      }, controller.signal);
      // The PIN response may arrive after cancel, a new selection, or unmount. Never continue that operation.
      if (!current()) return;
      mutationStarted = true;
      if (selected.kind === "quantity") await productionGateway.voidQuantity(selected.run.id, selected.entry.id, note, approval.approvalToken, undefined, controller.signal);
      else await productionGateway.assignDowntimeReason(selected.run.id, selected.downtime.id, { reasonCodeId: code, note, approvalToken: approval.approvalToken }, undefined, controller.signal);
      if (!current()) return;
      notify(selected.kind === "quantity" ? "Hatalı miktar kaydı geri alındı." : "Duruş kaydı düzeltildi.");
      setTarget(null); setReason(""); setReasonCodeId("");
      void load(); void onSaved().catch(() => { if (lifecycle.current.live) setFormError("Düzeltme kaydedildi; güncel liste alınamadı. Yenileyin."); });
    } catch (error) {
      if (!lifecycle.current.live || lifecycle.current.write !== write) return;
      setFormError(controller.signal.aborted
        ? mutationStarted ? "Düzeltmenin sonucu doğrulanamadı. Tekrar onaylamadan önce kaydı yenileyip kontrol edin; otomatik tekrar yapılmadı." : "Amir onayı zamanında alınamadı. Düzeltme isteği gönderilmedi."
        : error instanceof Error ? error.message : "Düzeltme sonucu doğrulanamadı.");
    } finally {
      window.clearTimeout(timeout); if (request.current === controller) request.current = null;
      if (lifecycle.current.live && lifecycle.current.write === write) setSaving(false);
    }
  };
  const itemCount = runs.reduce((total, run) => total + operatorQuantityRecords(run.entries).filter((entry) => !entry.voidedAt).length + run.downtimes.filter((downtime) => downtime.endedAt).length, 0);
  const changeDate = (nextDate: string) => {
    cancelWrite(); lifecycle.current.read++; setDate(nextDate); setRuns([]); setTarget(null); setFormError(null);
  };
  const clearSelection = () => { cancelWrite(); setTarget(null); };
  return { date, reasons, runs, target, reason, setReason, reasonCodeId, setReasonCodeId, supervisorPin, setSupervisorPin, loading, saving, formError, itemCount, dismiss, load, choose, submit, changeDate, clearSelection };
}
